import React, {
    useCallback, useEffect, useContext, useState
} from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import hoistNonReactStatic from 'hoist-non-react-statics';
import { useAuthentication } from 'gw-digital-auth-react';
import { PolicyService } from 'gw-capability-policy';
import { UsersProfileDetailsService } from 'gw-capability-profileinfo';
import { AuthorisedUserService } from 'tdic-capability-authorised-user';
import _ from 'lodash';
import { error } from '@jutro/logger';

export const ApplicationPropertyContext = React.createContext({
    tncTransactionTypes: []
});
function ApplicationPropertyContextProvider({ children }) {
    const { authHeader } = useAuthentication();
    const history = useHistory();
    const [tncTransactionTypesSubTypes, setTncTransactionTypesSubTypes] = useState([]);
    const [tncAcceptedStatus, setTnCAcceptedStatus] = useState(false);
    const [termData, setTermData] = useState({});
    const [isTnCCalled, setTnCCalled] = useState(false);
    const [isUserRoleCalled, updateUserRollCalled] = useState(false);
    const [isUserContactSummaryCalled, updateUserContactSummaryCalled] = useState(false);
    const [isAuthorisedUser, updateAuthorisedUser] = useState(null);
    const [loggedUserContact, updateLoggedUserContact] = useState(null);
    const [enrolledPolicy, updateEnrolledPolicy] = useState('');

    const getEffectiveUserContactSummary = useCallback(() => {
        updateUserContactSummaryCalled(true);
        UsersProfileDetailsService.getEffectiveUserContactSummary(
            authHeader
        ).then((contact) => {
            updateLoggedUserContact(contact);
        }).catch((err) => {
            error(`TdicApplicationHOCContext - getEffectiveUserContactSummary - Unable to get effective user contact summary ${JSON.stringify(err)}`);
            return history.push('/app-error');
        });
    }, [authHeader, history]);
    useEffect(() => {
        if (!_.isUndefined(authHeader) && !isTnCCalled) {
            setTnCCalled(true);
            PolicyService.getTnCTransactionTypesSubTypes(
                authHeader
            ).then((subTypes) => {
                setTncTransactionTypesSubTypes(subTypes);
            }).catch((err) => {
                error(`TdicApplicationHOCContext - getTnCTransactionTypesSubTypes - Unable to get TnC Transaction Sub Types ${JSON.stringify(err)}`);
                return history.push('/app-error');
            });
        }
        if (!_.isUndefined(authHeader) && !isUserRoleCalled) {
            updateUserRollCalled(true);
            AuthorisedUserService.isEffectiveUserAnAuthorisedUser(
                authHeader
            ).then((role) => {
                updateAuthorisedUser(role);
            }).catch((err) => {
                error(`TdicApplicationHOCContext - getTnCTransactionTypesSubTypes - Unable to get TnC Transaction Sub Types ${JSON.stringify(err)}`);
                return history.push('/app-error');
            });
        }
        if (!_.isUndefined(authHeader) && !isUserContactSummaryCalled) {
            getEffectiveUserContactSummary();
        }
    }, [authHeader, getEffectiveUserContactSummary, history, isTnCCalled,
        isUserContactSummaryCalled, isUserRoleCalled, setTncTransactionTypesSubTypes]);

    return (
        <ApplicationPropertyContext.Provider
            value={{
                tncTransactionTypesSubTypes,
                tncAcceptedStatus,
                setTnCAcceptedStatus,
                termData,
                setTermData,
                updateEnrolledPolicy,
                enrolledPolicy,
                isAuthorisedUser,
                loggedUserContact,
                getEffectiveUserContactSummary
            }}
        >
            {children}
        </ApplicationPropertyContext.Provider>
    );
}
ApplicationPropertyContextProvider.propTypes = {
    children: PropTypes.shape({}).isRequired,
    authHeader: PropTypes.shape({}).isRequired
};

function getDisplayName(WrappedComponent) {
    return WrappedComponent.displayName || WrappedComponent.name || 'Component';
}
export function withApplicationPropertyContext(WrappedComponent) {
    function WithApplicationPropertyContext(props) {
        const tncTypesProps = useContext(ApplicationPropertyContext);
        return <WrappedComponent {...tncTypesProps} {...props} />;
    }
    hoistNonReactStatic(WithApplicationPropertyContext, WrappedComponent);
    WithApplicationPropertyContext.displayName = `WithApplicationPropertyContext(${getDisplayName(WrappedComponent)})`;
    return WithApplicationPropertyContext;
}
export const ApplicationPropertyWithContextProvider = ApplicationPropertyContextProvider;

export default ApplicationPropertyContext.Consumer;
